import type { ParentChildAssignment } from '@repo/types/parent';

import { format } from 'date-fns';

import { getParentAssignmentState, ParentAssignmentStatusBadge } from '@/components/features/parent/ParentAssignmentStatus';
import { ParentGradeValue } from '@/components/features/parent/ParentGradeValue';
import { ParentStat } from '@/components/features/parent/ParentStat';
import { useParentDetailDialog } from '@/hooks/parent';

interface ParentAssignmentCardProps {
  assignment: ParentChildAssignment;
  childId: number;
}

/**
 * One assignment set to a child, with how they did on it.
 *
 * The backend filters submissions to this child, so anything returned is
 * theirs — a parent never sees another student's work.
 *
 * The row carries only what a parent scans a list for: what it was, when it was
 * due, the mark, and whether it was handed in. The descriptions — the
 * assignment's own and the teacher's feedback — are authored HTML of unbounded
 * length, so they open in a dialog instead of giving every row a different
 * height. Opening a row names it in the URL, which the page's dialog reads.
 */
export function ParentAssignmentCard(parentAssignmentCardProps: ParentAssignmentCardProps) {
  const { assignment, childId } = parentAssignmentCardProps;
  const { name } = assignment;

  const { setOpen } = useParentDetailDialog('assignment', childId, assignment.id);

  const state = getParentAssignmentState(assignment);
  const { submission, dueDate } = state;

  return (
    <li className={'border-border-primary bg-card/75 rounded-md border'}>
      {/* A real button, so the whole row is reachable by Tab and opens on
          Enter or Space without any key handling of our own. */}
      <button
        type={'button'}
        aria-haspopup={'dialog'}
        onClick={() => setOpen(true)}
        className={'outline-focus-ring hover:bg-accent/40 flex w-full cursor-pointer flex-col gap-2 rounded-md p-3 text-left outline-0 transition-colors focus-visible:outline-3'}
      >
        <div className={'flex w-full flex-col gap-2 md:flex-row'}>
          <div className={'flex min-w-0 flex-1 flex-col'}>
            <span className={'text-foreground text-sm font-medium'}>{name}</span>
            {dueDate ? <span className={'text-muted-foreground text-xs'}>Termin: {format(dueDate, 'd MMM yyyy, H:mm')}</span> : null}
          </div>

          <div className={'flex shrink-0 flex-wrap items-center gap-3'}>
            <ParentGradeValue grade={submission?.grade ?? null} />

            <ParentStat label={'Status'}>
              <ParentAssignmentStatusBadge state={state} />
            </ParentStat>
          </div>
        </div>

        {/* The row's own text names the assignment; this only says what
            activating it does, which nothing visible would otherwise. */}
        <span className={'sr-only'}>Pokaż szczegóły zadania</span>
      </button>
    </li>
  );
}
